<template>
  <div class="flex min-h-screen flex-col justify-center px-6 py-12 lg:px-8 bg-gradient-to-br from-red-600 to-indigo-600">
    <div class="sm:mx-auto sm:w-full sm:max-w-sm">
      <a href="https://www.lumbredocs.com.ar" target="_blank" rel="noopener noreferrer">
        <img class="mx-auto h-10 w-auto" :src="logoHomeImg" alt="Logo" />
      </a>
      <h2 v-if="!loading" class="mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-white">{{$t('activate_your_account')}}</h2>
      <h2 v-else class="mt-2 text-center text-2xl font-bold leading-9 tracking-tight text-white">{{$t('loading')}}</h2>
    </div>
    <div v-if="!loading" class="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
      <form class="space-y-6" @submit.prevent="saveNewPassword">
        <label for="email" class="block text-sm font-medium leading-6 text-white">{{$t('password')}}</label>
        <div class="position-relative">
          <input
            :type="showInputValue ? 'text' : 'password'"
            name="password"
            class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-100 sm:text-sm sm:leading-6 pl-2"
            :class="{ 'error': $v.form.newPassword.$error }"
            v-model="form.newPassword"
            @input="evaluatePassword"
            tabindex="1"
          >
          <font-awesome-icon
            id="font-awesome-icon-eye"
            v-if="!showInputValue"
            icon="eye"
            size="sm"
            class="clickable eyes"
            @click="showInputValue = true"
            />
          <font-awesome-icon
            id="font-awesome-icon-eye-slash"
            v-if="showInputValue"
            icon="eye-slash"
            size="sm"
            class="clickable eyes"
            @click="showInputValue = false"
            />
        </div>
        <small class="lm-input-caption" v-if="$v.form.newPassword.$error">Campo inválido</small>
        <div class="block text-sm font-medium leading-6 text-white">
          <p v-if="passwordStrength">
            Fortaleza de la contraseña: {{ passwordScoreText }}
          </p>
          <p v-if="passwordStrength && passwordStrength.feedback.suggestions.length">
            Sugerencias:
            <ul>
              <li v-for="suggestion in passwordStrength.feedback.suggestions" :key="suggestion">{{ suggestion }}</li>
            </ul>
          </p>
        </div>
        <label for="email" class="block text-sm font-medium leading-6 text-white">{{$t('password_again')}}</label>
        <div class="position-relative">
          <input
            :type="showInputValue ? 'text' : 'password'"
            class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-100 sm:text-sm sm:leading-6 pl-2"
            :class="{ 'error': $v.form.newPasswordAgain.$error }"
            v-model.lazy="$v.form.newPasswordAgain.$model"
            tabindex="2"
          >
          <font-awesome-icon
            id="font-awesome-icon-eye"
            v-if="!showInputValue"
            icon="eye"
            size="sm"
            class="clickable eyes"
            @click="showInputValue = true"
            />
          <font-awesome-icon
            id="font-awesome-icon-eye-slash"
            v-if="showInputValue"
            icon="eye-slash"
            size="sm"
            class="clickable eyes"
            @click="showInputValue = false"
            />
        </div>
        <small class="block text-sm font-medium leading-6 text-white" v-if="$v.form.newPasswordAgain.$error">Las contraseñas son diferentes</small>
        <button type="submit"
          class="mt-4 flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          :disabled="disableSubmit">
          <template v-if="!loading">
            Guardar
          </template>
          <template v-else>
            <i class="mdi mdi-loading lm-button-loading"></i>
          </template>
        </button>
      </form>
      <a href="https://www.lumile.com.ar" target="_blank" rel="noopener noreferrer">
        <img class="mt-5 mx-auto h-5 w-auto" :src="logoLumileWhite" alt="Logo">
      </a>
    </div>
  </div>
</template>

<script>
import LmTextInput from '@/components/form/elements/LmTextInput'
import { mapState, mapGetters, mapActions } from 'vuex'
import { required, sameAs } from 'vuelidate/lib/validators'
import commonsApi from '@/api/commons'
import lmToasts from '@/mixins/lmToasts.js'
import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core'
import * as zxcvbnCommonPackage from '@zxcvbn-ts/language-common'
import * as zxcvbnEsEsPackage from '@zxcvbn-ts/language-es-es'

export default {
  name: 'app',
  components: {
    LmTextInput
  },
  mixins: [lmToasts],
  data () {
    return {
      showInputValue: false,
      form: {
        newPassword: null,
        newPasswordAgain: null
      },
      minPasswordScore: 4,
      passwordStrength: null,
      serverErrorMessage: '',
      loading: false,
      restoredPasswordSuccessfully: false
    }
  },
  validations: {
    form: {
      newPassword: {
        required
      },
      newPasswordAgain: {
        sameAsNewPassword: sameAs('newPassword')
      }
    }
  },
  methods: {
    ...mapActions('notifications', ['getNotifications']),
    async saveNewPassword () {
      this.$v.$touch()
      if (!this.$v.form.$invalid) {
        this.loading = true
        try {
          await commonsApi.axiosPutWrapper(
            process.env?.VUE_APP_RESTORE_PASSWORD_URL + this.$route.params.token,
            {
              new_password: this.form.newPassword,
              new_password_again: this.form.newPasswordAgain
            }
          )
          this.restoredPasswordSuccessfully = true
          this.lmToastSuccessMessage('Tu usuario ya fue activado.  Podés ingresar con tu nueva contraseña.', 'Usuario activado')
          setTimeout(() => {
            this.$router.push('/login')
          }, 3000)
        } catch (err) {
          this.lmToastDangerMessage(err.response.data.message, `${err.response.data.type} - ${err.response.data.code}`)
        } finally {
          this.loading = false
        }
      }
    },
    evaluatePassword (el) {
      this.passwordStrength = zxcvbn(this.form.newPassword)
    }
  },
  computed: {
    ...mapGetters('session', ['isAuthenticated']),
    ...mapState('settings', ['appName', 'logoHomeImg', 'title1', 'title2', 'text', 'bgImage', 'loginBgColor', 'loginLogoWidth', 'loginLogoJustifyContent', 'loginTextAlign', 'logoLumileWhite']),
    authMode () {
      return process.env.VUE_APP_AUTH_MODE
    },
    disableSubmit () {
      return this.loading || this.passwordStrength?.score < this.minPasswordScore || this.$v.form.$invalid
    },
    passwordScoreText () {
      let scoreText
      if (this.passwordStrength?.score >= this.minPasswordScore) {
        scoreText = 'Fuerte'
      } else if (this.passwordStrength?.score === 0) {
        scoreText = 'Débil'
      } else {
        scoreText = 'Media'
      }
      return scoreText
    }

  },
  created () {
    if (this.isAuthenticated) {
      this.$router.replace('/')
    }
  },
  mounted () {
    this.$bvToast.hide('lm-toast-success-internal-link')
    const options = {
      translations: zxcvbnEsEsPackage.translations,
      graphs: zxcvbnCommonPackage.adjacencyGraphs,
      dictionary: {
        ...zxcvbnCommonPackage.dictionary,
        ...zxcvbnEsEsPackage.dictionary
      }
    }

    zxcvbnOptions.setOptions(options)
  }
}
</script>

<style scoped>
.eyes{
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 1;
  }
</style>
